/**
 * Redirect-After-Login Utility
 *
 * Preserves a target path across the login flow so users return to the page
 * they intended to visit (e.g. clicking a /waitlist/claim/:id email link
 * while logged out).
 *
 * Two channels are needed because OAuth (Cognito Hosted UI redirect)
 * round-trips through an external domain and loses URL state:
 *   - sessionStorage: survives OAuth round-trip
 *   - URL ?redirect=  : initial entry point
 *
 * Only same-origin relative paths are allowed to prevent open-redirect.
 */

const STORAGE_KEY = 'post_login_redirect';

/**
 * Returns the path if it is safe to redirect to, otherwise null.
 * Allowed: starts with "/", is not protocol-relative ("//"), no scheme.
 */
export function sanitizeRedirectPath(path: string | null | undefined): string | null {
  if (!path) return null;
  if (typeof path !== 'string') return null;
  // Must start with single "/" — protocol-relative ("//evil.com") and
  // absolute URLs ("http://...") are rejected.
  if (!path.startsWith('/')) return null;
  if (path.startsWith('//')) return null;
  // When parsing a URL, browsers drop tabs and newlines and read a backslash as "/",
  // so "/<newline>/evil.com" becomes "//evil.com". A real path is percent-encoded,
  // so a control character, space or backslash anywhere means a crafted value.
  for (let i = 0; i < path.length; i++) {
    const c = path.charCodeAt(i);
    if (c <= 0x20 || c === 0x7f || c === 0x5c) return null;
  }
  // Disallow embedded scheme (defense in depth)
  if (/^\/[a-z][a-z0-9+\-.]*:/i.test(path)) return null;
  return path;
}

/**
 * Read ?redirect= from current URL and return sanitized path or null.
 */
export function readRedirectFromQuery(): string | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  return sanitizeRedirectPath(params.get('redirect'));
}

/**
 * Persist a redirect target into sessionStorage so it survives an OAuth
 * round-trip. Only sanitized paths are stored. Calling with a nullish or
 * unsafe value clears any previously-stored target so a stale redirect from
 * an earlier abandoned/failed flow does not leak into the next login.
 */
export function rememberRedirect(path: string | null | undefined): void {
  if (typeof window === 'undefined') return;
  const safe = sanitizeRedirectPath(path);
  if (safe) {
    window.sessionStorage.setItem(STORAGE_KEY, safe);
  } else {
    window.sessionStorage.removeItem(STORAGE_KEY);
  }
}

/**
 * Pop a previously remembered redirect (one-shot read + clear).
 * Returns null if missing or unsafe.
 */
export function consumeRedirect(): string | null {
  if (typeof window === 'undefined') return null;
  const raw = window.sessionStorage.getItem(STORAGE_KEY);
  window.sessionStorage.removeItem(STORAGE_KEY);
  return sanitizeRedirectPath(raw);
}
